import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import arthereumProvider from "../../../backend/ArthereumProvider";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { actions } from "../../redux";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "../../components/LoadingIndicator";
import CommonButton from "../../components/CommonButton";
import { v4 } from "uuid";
import { THEME_COLORS } from "../../themes/DarkLight";
import { FONTSIZE, imagesUrls } from "../../../constants";
import getUserSignInData from "../../utils/getUserSignInData";
import { CheckmarkDoneCircleOutline } from "react-ionicons";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import eventBus from "../../../utils/eventBus";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

const ClaimGrantPage = () => {
  useEffect(() => {
    eventBus.emit("fullscreen:enter");
    return () => {
      eventBus.emit("fullscreen:exit");
    };
  }, []);

  const userData = useSelector((state) => (state as any)?.actualUser);
  const dispatch = useDispatch();

  const grantData = {
    id: "ID-" + v4(),
    date: new Date().toISOString(),
    amount: userData?.miningInfos?.nextGrantValue,
  };

  const theme = useThemeHook();
  const [isAccepting, setIsAccepting] = useState(false);
  const navigate = useNavigate();
  const getTranslationText = useTranslationHook();

  const handleClaimClick = () => {
    if (isAccepting) return;
    
    emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
      modal: {
        name: "preGrantAdModal",
        modalData: {
          grantAmount: userData?.miningInfos?.nextGrantValue,
          onClaim: (isBoosted: boolean) => accept(isBoosted),
        },
      },
    });
  };

  const accept = async (isBoosted: boolean) => {
    try {
      if (isAccepting) {
        return;
      }
      const { grantValue, multiplier, piGiveaway } = await arthereumProvider.acceptGrant(userData?.user?.id, isBoosted);
      
      // Update Redux state locally instead of calling signIn() again.
      // signIn() was doing a full re-login (2-3 extra Firestore reads) per grant.
      const updatedUser = {
        ...userData,
        user: {
          ...userData.user,
          arthereumBalance: (userData.user.arthereumBalance || 0) + grantValue,
          picoinBalance: (userData.user.picoinBalance || 0) + piGiveaway, // fallback for existing data
          miningSessionsCount: (userData.user.miningSessionsCount || 0) + 1,
          lastMiningDate: { seconds: Math.floor(Date.now() / 1000) },
        },
        miningInfos: {
          ...userData.miningInfos,
          nextGrantDate: new Date(Date.now() + 2 * 60 * 60 * 1000),
        },
      };
      dispatch(actions.setActualUser(updatedUser));

      // Show Viral Share Modal if boost is significant (>= 1.5x)
      if (multiplier >= 1.5) {
        emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
          modal: {
            name: "viralShareModal",
            modalData: {
              grantAmount: grantValue,
              multiplier: multiplier,
            }
          }
        });
      }

      // Award contest points for claiming grant (+3 points)
      const contestResult = await arthereumProvider.awardContestPoints(
        userData?.user?.id,
        "grant"
      );

      // Show contest points modal if points were earned
      if (contestResult.pointsAwarded > 0) {
        emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
          modal: {
            name: "contestPointsModal",
            modalData: {
              pointsEarned: contestResult.pointsAwarded,
              totalPoints: contestResult.totalPoints,
              action: "grant",
              isInContest: contestResult.isContestant,
              giveawayMessage: `Pi Winter Giveaway: Received +${piGiveaway.toFixed(4)} Pi Coin!`,
            },
          },
        });
      } else {
        // Show regular grant accepted modal if not in contest
        emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
          modal: {
            name: "alertModal",
            modalData: {
              description: `${getTranslationText("grant_accepted_desc")}\n\n❄️ Pi Winter Giveaway: You also received +${piGiveaway.toFixed(4)} Pi Coin!`,
              title: getTranslationText("grant_accepted"),
              icon: (
                <CheckmarkDoneCircleOutline
                  color={THEME_COLORS[theme].piPurple}
                  height={"50px"}
                  width={"50px"}
                />
              ),
            },
          },
        });
      }
      
      // Show rewarded ad after claiming (with delay)
      setTimeout(() => {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") return;
        piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id);
      }, 1000);

      navigate(-1);
    } catch (error) {
      console.log(error);
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className="max-w-2xl mx-auto px-6 py-8 md:py-12">
        {/* Header Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-blue-500 opacity-20 blur-2xl rounded-full"></div>
            <img
              src={imagesUrls["arthereum"]}
              alt="Arthereum"
              className="relative w-24 h-24 md:w-32 md:h-32 animate-pulse"
            />
          </div>
          <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
            {getTranslationText("accept_grant")}
          </h1>
          <p className={`text-sm md:text-base text-center max-w-md ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
            {getTranslationText("accept_grant_desc")}
          </p>
        </div>

        {/* Pi Winter Giveaway Banner */}
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-blue-600/10 via-indigo-600/10 to-purple-600/10 border border-blue-500/20 text-center relative overflow-hidden backdrop-blur-sm shadow-inner">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-2xl pointer-events-none"></div>
          <p className="text-xs font-black uppercase text-blue-500 tracking-widest flex items-center justify-center gap-1.5 mb-1 animate-pulse">
            ❄️ Pi Winter Giveaway Active!
          </p>
          <p className={`text-xs ${theme === "dark" ? "text-gray-300" : "text-gray-600"} leading-relaxed font-semibold`}>
            Claim your grant now and instantly receive <span className="text-indigo-500 font-bold">1/100 (1%)</span> of its value in <span className="font-bold text-amber-500">Pi Network Coins (Pi)</span>!
          </p>
        </div>

        {/* Grant Card */}
        <div className="mb-8">
          <div className={`relative rounded-2xl p-6 md:p-8 border backdrop-blur-sm ${
            theme === "dark"
              ? "bg-gray-800 border-blue-500/30 shadow-xl shadow-blue-500/10"
              : "bg-white border-blue-300/50 shadow-lg"
          }`}>
            {/* Decorative gradient overlay */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-2xl pointer-events-none"></div>
            
            {/* Card Header */}
            <div className="relative flex justify-between items-start mb-6">
              <div className="flex-1">
                <p className={`text-xs font-mono mb-1 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}>
                  {grantData.id}
                </p>
                <p className={`text-xs ${theme === "dark" ? "text-gray-600" : "text-gray-500"}`}>
                  {grantData.date}
                </p>
              </div>
              <img
                src={imagesUrls["arthereum"]}
                alt="Seal"
                className="w-8 h-8 opacity-80"
              />
            </div>

            {/* Amount Display */}
            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  {grantData?.amount?.toFixed(2)}
                </span>
                <span className="text-xl md:text-2xl font-semibold text-blue-500">ATH</span>
              </div>
              <p className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                {getTranslationText("grant_amount", {
                  amount: grantData?.amount?.toFixed(2),
                })}
              </p>
            </div>

            {/* Divider */}
            <div className={`h-px mb-6 ${theme === "dark" ? "bg-gray-700" : "bg-gray-200"}`}></div>

            {/* Grant Details */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                  {getTranslationText("grant_to")}
                </span>
                <span className={`text-sm font-semibold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  {userData?.user?.username}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                  {getTranslationText("date")}
                </span>
                <span className={`text-sm font-mono ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                  {new Date(grantData.date).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Tech Pattern Decoration */}
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50"></div>
          </div>
        </div>

        {/* Action Button */}
        <div className="space-y-4">
          {isAccepting && (
            <div className="flex justify-center">
              <LoadingIndicator />
            </div>
          )}
          <CommonButton
            padding={0}
            onClickHandle={handleClaimClick}
            text={getTranslationText("claim")}
          />
        </div>

        {/* Footer Info */}
        <div className={`mt-8 p-4 rounded-lg text-center ${
          theme === "dark" ? "bg-gray-800/50" : "bg-blue-50"
        }`}>
          <p className={`text-xs ${theme === "dark" ? "text-gray-500" : "text-gray-600"}`}>
            Secured by Arthereum Protocol
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClaimGrantPage;